import { OpenAIRequestOptions, OpenAIResponse, SimilarCompaniesResponse, SimilarCompany } from './types';
import Papa from 'papaparse';
import { post } from '../api';

/**
 * Backend proxy endpoint for OpenAI API
 */
const OPENAI_PROXY_ENDPOINT = '/external/openai/chat/completions';

/**
 * Default model to use for OpenAI requests
 */
const DEFAULT_MODEL = 'gpt-4';

/**
 * Send a prompt to the OpenAI API via backend proxy
 * @param prompt The prompt to send to OpenAI
 * @param options Additional options for the request
 * @returns The response from OpenAI
 */
export const sendPrompt = async (
  prompt: string,
  options: OpenAIRequestOptions = {}
): Promise<string> => {
  try {
    // Use the API service which already handles authentication
    const data = await post<OpenAIResponse>(OPENAI_PROXY_ENDPOINT, {
      model: options.model || DEFAULT_MODEL,
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: options.temperature ?? 0.7,
      max_tokens: options.maxTokens,
      top_p: options.topP,
      frequency_penalty: options.frequencyPenalty,
      presence_penalty: options.presencePenalty
    });
    
    if (!data.choices || data.choices.length === 0) {
      throw new Error('No response from OpenAI API');
    }

    // Return the content from the first choice
    return data.choices[0].message?.content || '';
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    throw error;
  }
};

/**
 * Enhance a project description using AI
 * @param description The original project description
 * @returns Enhanced description
 */
export const enhanceDescription = async (description: string): Promise<string> => {
  const prompt = `Please enhance the following project description to make it more professional, 
  detailed, and compelling. Maintain the core information but improve clarity, structure, and impact. 
  Add relevant details that would be valuable for a location analysis project.
  
  Original description:
  ${description}
  
  Enhanced description:`;
  
  return await sendPrompt(prompt, {
    temperature: 0.7,
    maxTokens: 1000
  });
};

/**
 * Format the prompt for similar companies query
 * @param companyName The name of the company
 * @param contactPerson The key contact person of the company
 * @returns Formatted prompt string
 */
export const formatSimilarCompaniesPrompt = (
  companyName: string,
  contactPerson: string
): string => {
  return `Who are similar companies to ${companyName} Company (past/current key contact person: ${contactPerson})

1) Please list 5 close similar companies who are focused in similar business and Who are key contact person of the concerned company? Make sure suggest similar size companies. If you dont find the key contact dont assume list it as N/A

I want information for all 6 companies (1 main + 5 similar who are into similar business).

IMPORTANT: Format your response as a JSON array of objects with the following structure:
[
  {
    "company": "${companyName}",
    "description": "Description of ${companyName}...",
    "equipment": "Equipment list...",
    "website": "website-url",
    "contactPerson": "${contactPerson}"
  },
  {
    "company": "SimilarCompany1",
    "description": "Description of SimilarCompany1...",
    "equipment": "Equipment list...",
    "website": "website-url",
    "contactPerson": "Contact Person"
  }
]

After the array, include a separate object for common equipment:
{
  "commonEquipment": "List of common equipment across all companies..."
}

Make sure to properly escape any special characters in the JSON. Do not include any text before or after the JSON data.`;
};

/**
 * Get similar companies to the specified company
 * @param companyName The name of the company
 * @param contactPerson The key contact person of the company
 * @returns Structured data about similar companies
 */
export const getSimilarCompanies = async (
  companyName: string,
  contactPerson: string
): Promise<SimilarCompaniesResponse> => {
  const prompt = formatSimilarCompaniesPrompt(companyName, contactPerson);
  const response = await sendPrompt(prompt, { 
    temperature: 0.2,
    topP: 0.1
  });
  
  return parseResponse(response, companyName, contactPerson);
};

/**
 * Parse response from OpenAI into structured data
 * @param response The response from OpenAI
 * @param originalCompanyName The original company name (for fallback)
 * @param originalContactPerson The original contact person (for fallback)
 * @returns Structured data about similar companies
 */
export const parseResponse = (
  response: string,
  originalCompanyName: string,
  originalContactPerson: string
): SimilarCompaniesResponse => {
  try {
    console.log('Raw OpenAI response:', response);
    
    // Try to parse the response as JSON
    // First, extract the JSON part from the response
    let jsonStr = response.trim();
    
    // Find the first '[' and the last ']' to extract the array part
    const arrayStartIndex = jsonStr.indexOf('[');
    const arrayEndIndex = jsonStr.lastIndexOf(']');
    
    if (arrayStartIndex !== -1 && arrayEndIndex !== -1 && arrayEndIndex > arrayStartIndex) {
      // Extract the array part
      const arrayStr = jsonStr.substring(arrayStartIndex, arrayEndIndex + 1);
      
      // Parse the array
      const companiesArray = JSON.parse(arrayStr);
      
      // Find the common equipment object
      let commonEquipment = '';
      const commonEquipmentMatch = jsonStr.match(/{\s*"commonEquipment"\s*:\s*"([^"]+)"\s*}/);
      if (commonEquipmentMatch && commonEquipmentMatch[1]) {
        commonEquipment = commonEquipmentMatch[1];
      }
      
      // Extract main company (first item)
      const mainCompany: SimilarCompany = companiesArray.length > 0 ? {
        name: companiesArray[0].company || originalCompanyName,
        description: companiesArray[0].description || '',
        equipment: companiesArray[0].equipment || '',
        website: companiesArray[0].website || '',
        contactPerson: companiesArray[0].contactPerson || originalContactPerson
      } : {
        name: originalCompanyName,
        description: '',
        equipment: '',
        website: '',
        contactPerson: originalContactPerson
      };
      
      // Extract similar companies (next items)
      const similarCompanies: SimilarCompany[] = companiesArray.slice(1).map((item: any) => ({
        name: item.company || '',
        description: item.description || '',
        equipment: item.equipment || '',
        website: item.website || '',
        contactPerson: item.contactPerson || 'N/A'
      }));
      
      return {
        mainCompany,
        similarCompanies,
        commonEquipment
      };
    }
    
    // If we couldn't parse the JSON properly, try the CSV parsing
    console.log('Failed to parse JSON, falling back to CSV parsing');
    return parseCSVResponse(response, originalCompanyName, originalContactPerson);
  } catch (error) {
    console.error('Error parsing JSON response:', error);
    console.error('Original response:', response);
    
    // Fall back to CSV parsing
    try {
      return parseCSVResponse(response, originalCompanyName, originalContactPerson);
    } catch (csvError) {
      console.error('CSV parsing also failed:', csvError);
      
      // Return a fallback structure if all parsing fails
      return {
        mainCompany: {
          name: originalCompanyName,
          description: '',
          equipment: '',
          website: '',
          contactPerson: originalContactPerson
        },
        similarCompanies: [],
        commonEquipment: ''
      };
    }
  }
};

/**
 * Parse CSV response from OpenAI into structured data
 * @param response The CSV response from OpenAI
 * @param originalCompanyName The original company name (for fallback)
 * @param originalContactPerson The original contact person (for fallback)
 * @returns Structured data about similar companies
 */
export const parseCSVResponse = (
  response: string,
  originalCompanyName: string,
  originalContactPerson: string
): SimilarCompaniesResponse => {
  try {
    console.log('Raw OpenAI response:', response);
    
    // Preprocess the response to handle the format from OpenAI
    // The response might be in a format with quotes and newlines that needs cleaning
    let processedResponse = response;
    
    // If the response doesn't start with headers, add them
    if (!response.trim().startsWith('Company,Description')) {
      // Extract data from the response format
      const lines = response.split('\n').filter(line => line.trim() !== '');
      
      // Create a properly formatted CSV
      const formattedLines = lines.map(line => {
        // Remove any starting/ending quotes
        let cleanLine = line.trim();
        if (cleanLine.startsWith('"')) {
          cleanLine = cleanLine.substring(1);
        }
        if (cleanLine.endsWith('"')) {
          cleanLine = cleanLine.substring(0, cleanLine.length - 1);
        }
        
        // Split by commas, but be careful about commas within quotes
        const parts: string[] = [];
        let currentPart = '';
        let inQuotes = false;
        
        for (let i = 0; i < cleanLine.length; i++) {
          const char = cleanLine[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
            currentPart += char;
          } else if (char === ',' && !inQuotes) {
            parts.push(currentPart.trim());
            currentPart = '';
          } else {
            currentPart += char;
          }
        }
        
        // Add the last part
        if (currentPart) {
          parts.push(currentPart.trim());
        }
        
        // Ensure we have 5 columns
        while (parts.length < 5) {
          parts.push('');
        }
        
        return parts.join(',');
      });
      
      // Add headers
      processedResponse = 'Company,Description,Equipment,Website,ContactPerson\n' + formattedLines.join('\n');
    }
    
    console.log('Processed response for parsing:', processedResponse);
    
    // Parse CSV data with more flexible options
    const parsedData = Papa.parse(processedResponse, {
      header: true,
      skipEmptyLines: true,
      delimiter: ',',
      quoteChar: '"',
      escapeChar: '"',
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim()
    });
    
    console.log('Parsed data:', parsedData);
    
    if (!parsedData.data || parsedData.data.length === 0) {
      console.error('Failed to parse CSV data. Errors:', parsedData.errors);
      throw new Error('Failed to parse CSV data from OpenAI response');
    }

    const companies = parsedData.data as any[];
    
    // Manual parsing as fallback if the structure doesn't match expectations
    if (!companies[0]?.Company && !companies[0]?.Description) {
      console.log('Standard parsing failed, attempting manual parsing...');
      return manualParseResponse(response, originalCompanyName, originalContactPerson);
    }
    
    // Extract main company (first row)
    const mainCompany: SimilarCompany = {
      name: companies[0]?.Company || originalCompanyName,
      description: companies[0]?.Description || '',
      equipment: companies[0]?.Equipment || '',
      website: companies[0]?.Website || '',
      contactPerson: companies[0]?.ContactPerson || originalContactPerson
    };
    
    // Extract similar companies (next 5 rows)
    const similarCompanies: SimilarCompany[] = companies
      .slice(1, 6)
      .filter(company => company.Company && company.Company !== 'CommonEquipment')
      .map(company => ({
        name: company.Company || '',
        description: company.Description || '',
        equipment: company.Equipment || '',
        website: company.Website || '',
        contactPerson: company.ContactPerson || 'N/A'
      }));
    
    // Extract common equipment (last row)
    const commonEquipmentRow = companies.find(row => row.Company === 'CommonEquipment');
    const commonEquipment = commonEquipmentRow?.Description || '';
    
    return {
      mainCompany,
      similarCompanies,
      commonEquipment
    };
  } catch (error) {
    console.error('Error parsing CSV response:', error);
    console.error('Original response:', response);
    
    // Try manual parsing as a last resort
    try {
      return manualParseResponse(response, originalCompanyName, originalContactPerson);
    } catch (manualError) {
      console.error('Manual parsing also failed:', manualError);
      
      // Return a fallback structure if all parsing fails
      return {
        mainCompany: {
          name: originalCompanyName,
          description: '',
          equipment: '',
          website: '',
          contactPerson: originalContactPerson
        },
        similarCompanies: [],
        commonEquipment: ''
      };
    }
  }
};

/**
 * Manual parsing for when PapaParse fails
 * This is a fallback method to extract data from the OpenAI response
 */
const manualParseResponse = (
  response: string,
  originalCompanyName: string,
  originalContactPerson: string
): SimilarCompaniesResponse => {
  console.log('Attempting manual parsing of response');
  
  // Split the response into lines
  const lines = response.split('\n').filter(line => line.trim() !== '');
  
  // Process each line to extract company data
  const companies: SimilarCompany[] = [];
  let commonEquipment = '';
  
  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) continue;
    
    // Check if this is the common equipment line
    if (line.includes('CommonEquipment')) {
      const parts = line.split(',');
      if (parts.length > 1) {
        commonEquipment = parts[1].trim();
      }
      continue;
    }
    
    // Extract company data
    // Format is expected to be: Company, Description, Equipment, Website, ContactPerson
    const parts = line.split(',');
    
    if (parts.length >= 3) {
      const company: SimilarCompany = {
        name: parts[0].replace(/"/g, '').trim(),
        description: parts.length > 1 ? parts[1].replace(/"/g, '').trim() : '',
        equipment: parts.length > 2 ? parts[2].replace(/"/g, '').trim() : '',
        website: parts.length > 3 ? parts[3].replace(/"/g, '').trim() : '',
        contactPerson: parts.length > 4 ? parts[4].replace(/"/g, '').trim() : 'N/A'
      };
      
      companies.push(company);
    }
  }
  
  // If we couldn't parse any companies, use the original company as fallback
  if (companies.length === 0) {
    companies.push({
      name: originalCompanyName,
      description: '',
      equipment: '',
      website: '',
      contactPerson: originalContactPerson
    });
  }
  
  // First company is the main one, the rest are similar companies
  const mainCompany = companies[0];
  const similarCompanies = companies.slice(1);
  
  return {
    mainCompany,
    similarCompanies,
    commonEquipment
  };
};

/**
 * Convert similar companies data to CSV format for download
 * @param data The similar companies data
 * @returns CSV string
 */
export const convertToCSV = (data: SimilarCompaniesResponse): string => {
  const allCompanies = [data.mainCompany, ...data.similarCompanies];
  
  const csvData = allCompanies.map(company => ({
    Company: company.name,
    Description: company.description,
    Equipment: company.equipment,
    Website: company.website,
    ContactPerson: company.contactPerson
  }));
  
  // Add common equipment as the last row
  csvData.push({
    Company: 'CommonEquipment',
    Description: data.commonEquipment,
    Equipment: '',
    Website: '',
    ContactPerson: ''
  });
  
  return Papa.unparse(csvData);
};
